import { useState } from "react";
import { getActiveDomain, listAccountConfigs } from "../api/config";
import { batchCreateUsers, createUser, listUsers } from "../api/users";
import type { BatchCreateSummary, CreateUserRequest } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { parseCsvUsers } from "./csvParser";
import { generateUsers } from "./nameGenerator";

const TIERS = ["pro", "proplus", "promax", "power"];

/**
 * v2.30(修复项1):单次提交给 `POST /users/batch` 的最大条数。
 *
 * 背景:该接口挂在 API Gateway 后面,集成超时固定 29 秒(硬上限,不可调大,
 * 与 Lambda 自身配置的 300 秒无关)。建号没有批量 API,每个用户都要串上
 * `CreateUser` + `CreateGroupMembership` 两次跨账号 identitystore 调用,
 * 后端线程池并发上限 8,总耗时随行数线性增长。实测 10 条没问题,但一次性提交
 * 100 条以上就会逼近/超过 29 秒——而**超时后 Lambda 并不会被打断**,用户其实
 * 还在后台继续创建,前端却只看到 504,运营会以为失败而重复操作。
 *
 * 改为固定小批次顺序提交:批次大小与总行数无关,每批耗时稳定在安全区间内,
 * 批次数量随行数线性增长——不管未来一次贴 50 条还是 500 条,都只是多跑几批,
 * 不存在"规模变大后又要重新解决一次超时"的问题。
 *
 * 取 25 的依据:按每用户 0.5~0.9 秒、并发 8 估算,25 条约 2~4 秒,即使 IdC 侧
 * 触发节流(`call_with_backoff` 会退避 1/2/4/8 秒)也仍有充足余量。同时刻意
 * 保持较小值以降低对 identitystore 的瞬时压力,避免触发 AWS 侧频控。
 */
const BATCH_CREATE_CHUNK_SIZE = 25;

/** v2.30(修复项2):批内重复行的定位信息,用于把"哪几行重复"直接指给运营。 */
type DuplicateRow = {
  index: number;
  field: "邮箱" | "用户名";
  value: string;
  firstSeenIndex: number;
};

/**
 * v2.30(修复项2):检查同批 CSV 内是否有重复行,按 (目标账号, 邮箱) 与
 * (目标账号, 用户名) 两个维度、大小写不敏感比对。
 *
 * 为什么必须拦在提交之前:
 *   - 相同邮箱不同用户名:两行都会去 IdC 建号,但台账主键是 (账号, 邮箱),
 *     只写得下一条 -> 留下一个**台账里不存在的孤儿 IdC 用户**,仍占订阅额度,
 *     平台侧看不见,一致性核对(方向是台账→IdC)也发现不了,只能人工去 IdC 捞。
 *   - 相同用户名:并发下一个正常创建、另一个走幂等分支,汇总会把 1 个真实用户
 *     报成 2 条成功,运营对账会算错数量。
 * 后端 `_find_intra_batch_duplicates` 有同样的校验兜底(直接调 API 的场景),
 * 前端前置是为了让运营立刻看到是哪几行重复,而不是拿一个 400 回来再猜。
 */
function findDuplicateRows(users: CreateUserRequest[]): DuplicateRow[] {
  const seenEmail = new Map<string, number>();
  const seenUsername = new Map<string, number>();
  const duplicates: DuplicateRow[] = [];

  users.forEach((u, idx) => {
    const accountId = (u.account_id ?? "").trim();
    const email = (u.email ?? "").trim().toLowerCase();
    const username = (u.username ?? "").trim().toLowerCase();

    if (email) {
      const key = `${accountId}\u0000${email}`;
      const first = seenEmail.get(key);
      if (first !== undefined) {
        duplicates.push({ index: idx, field: "邮箱", value: u.email ?? "", firstSeenIndex: first });
      } else {
        seenEmail.set(key, idx);
      }
    }

    if (username) {
      const key = `${accountId}\u0000${username}`;
      const first = seenUsername.get(key);
      if (first !== undefined) {
        duplicates.push({ index: idx, field: "用户名", value: u.username ?? "", firstSeenIndex: first });
      } else {
        seenUsername.set(key, idx);
      }
    }
  });

  return duplicates;
}

/**
 * 建号页,对应产品文档 §3.2。
 * 支持单条表单建号,以及粘贴 CSV 文本批量建号(不引入文件上传组件依赖,
 * 直接用 textarea 粘贴 CSV 内容更轻量)。
 * CSV 列顺序: account_id,username,given_name,family_name,email,tier
 */
export function CreateUserPage() {
  const accounts = useAsync(() => listAccountConfigs(), []);

  const [form, setForm] = useState<CreateUserRequest>({
    account_id: "",
    username: "",
    given_name: "",
    family_name: "",
    email: "",
    tier: "pro",
    auto_activate: true,
  });
  const [singleResult, setSingleResult] = useState<string | null>(null);
  const [singleError, setSingleError] = useState<string | null>(null);
  const [singleLoading, setSingleLoading] = useState(false);

  const [csvText, setCsvText] = useState("");
  const [batchResult, setBatchResult] = useState<BatchCreateSummary | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  // 批量建号"是否自动激活"默认值:应用于 CSV 里没有显式写第 7 列的行
  // (以及"自动生成用户信息"追加的新行),不强制覆盖用户在 CSV 里手动指定的值。
  const [batchAutoActivate, setBatchAutoActivate] = useState(true);
  // v2.30(修复项1):分批顺序提交时的进度(第几批/共几批、已处理/总数)
  const [batchProgress, setBatchProgress] = useState<{
    batchIndex: number;
    totalBatches: number;
    processed: number;
    total: number;
  } | null>(null);

  // ---- 自动生成用户信息 ----
  const [genAccountId, setGenAccountId] = useState("");
  const [genTier, setGenTier] = useState("pro");
  const [genCount, setGenCount] = useState(5);
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSingleError(null);
    setSingleResult(null);
    setSingleLoading(true);
    try {
      const result = await createUser(form);
      setSingleResult(
        `建号成功: ${result.email} (user_id=${result.user_id}${result.already_existed ? ", 该用户已存在,幂等跳过" : ""})`
      );
    } catch (err) {
      setSingleError(err instanceof Error ? err.message : "建号失败");
    } finally {
      setSingleLoading(false);
    }
  };

  /**
   * v2.30:批量建号提交流程改为「提交前查重 → 固定批次顺序提交 → 汇总累加」。
   *
   * 接口契约、后端校验/建号逻辑一律未改动,变的只是前端把整份 CSV 拆成
   * 多次请求(见 BATCH_CREATE_CHUNK_SIZE 的说明)。
   *
   * 某一批失败时中止后续批次(不再盲目往下建),但**保留已完成批次的汇总**并
   * 提示可直接重提整份 CSV——建号是幂等的(IdC 侧 ConflictException 回查复用、
   * 台账侧 ConditionalCheck 跳过),已成功的用户不会被重复创建。
   */
  const handleBatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBatchError(null);
    setBatchResult(null);
    setBatchProgress(null);

    const users = parseCsvUsers(csvText, batchAutoActivate);
    if (users.length === 0) {
      setBatchError("CSV 内容为空");
      return;
    }

    const duplicates = findDuplicateRows(users);
    if (duplicates.length > 0) {
      setBatchError(
        "CSV 内存在重复记录,未提交任何数据。请修正后重试:\n" +
          duplicates
            .map(
              (d) =>
                `· 第 ${d.index + 1} 行的${d.field}「${d.value}」与第 ${d.firstSeenIndex + 1} 行重复`
            )
            .join("\n")
      );
      return;
    }

    setBatchLoading(true);

    const batches: CreateUserRequest[][] = [];
    for (let i = 0; i < users.length; i += BATCH_CREATE_CHUNK_SIZE) {
      batches.push(users.slice(i, i + BATCH_CREATE_CHUNK_SIZE));
    }

    const acc: BatchCreateSummary = {
      total: 0,
      succeeded: 0,
      failed: 0,
      activation_dispatch_failed: 0,
      results: [],
    };
    setBatchProgress({
      batchIndex: 0,
      totalBatches: batches.length,
      processed: 0,
      total: users.length,
    });

    try {
      for (let i = 0; i < batches.length; i++) {
        const resp = await batchCreateUsers(batches[i]);
        acc.total += resp.total;
        acc.succeeded += resp.succeeded;
        acc.failed += resp.failed;
        acc.activation_dispatch_failed =
          (acc.activation_dispatch_failed ?? 0) + (resp.activation_dispatch_failed ?? 0);
        acc.results = acc.results.concat(resp.results);

        setBatchProgress({
          batchIndex: i + 1,
          totalBatches: batches.length,
          processed: acc.total,
          total: users.length,
        });
        // 每批完成就刷新一次结果表,让运营能边跑边看,而不是全部结束才出现
        setBatchResult({ ...acc, results: [...acc.results] });
      }
      // 全部批次成功后清空输入框,为下一个账号/下一批次建号做好准备。
      // 不在 finally 里清空:如果中途失败,已完成部分的汇总要保留、且运营可能
      // 需要修改 CSV 后重提,不应强制清空。
      setCsvText("");
    } catch (err) {
      setBatchResult({ ...acc, results: [...acc.results] });
      setBatchError(
        (err instanceof Error ? err.message : "批量建号失败") +
          `(已完成 ${acc.total}/${users.length} 条,剩余批次未提交。` +
          "已成功的用户不会重复创建,修正问题后可直接重新提交整份 CSV——建号是幂等的)"
      );
    } finally {
      setBatchLoading(false);
      setBatchProgress(null);
    }
  };

  /**
   * 自动生成用户信息:
   *   1. 读取当前生效收信域名(拼邮箱用)
   *   2. 读取该目标账号下已有用户的 username/email,连同当前输入框里
   *      已经存在的行一起作为去重集合,避免生成的用户名和数据库里已有的
   *      或本次还没提交的行重复
   *   3. 生成结果以 CSV 行的形式追加到输入框(不覆盖用户已经手动输入的内容)
   */
  const handleAutoGenerate = async () => {
    const accountId = genAccountId || accounts.data?.accounts[0]?.account_id || "";
    if (!accountId) {
      setGenError("请先选择目标账号");
      return;
    }
    if (genCount < 1 || genCount > 500) {
      setGenError("生成数量需在 1~500 之间");
      return;
    }
    setGenLoading(true);
    setGenError(null);
    try {
      const [domainResp, existingUsersResp] = await Promise.all([
        getActiveDomain(),
        // pageSize 拉大页:这里需要拿到该账号下的全部用户名做去重(§3.2),
        // 不能用列表页默认的每页 20 条(否则只对第一页去重,漏判)。
        listUsers(accountId, { pageSize: 1000 }),
      ]);
      const domain = domainResp.active_email_domain;
      if (!domain) {
        setGenError("尚未配置当前生效收信域名,请先在配置页设置(§4.1)");
        return;
      }

      // ⚠️ username 可能缺失:后端列表接口的字段投影是 `if k in item`,历史上
      // 存在过因并发写入产生的字段残缺记录(只有账号 ID/邮箱),这类记录返回的
      // JSON 里根本没有 username 字段,直接 .toLowerCase() 会抛
      // "Cannot read properties of undefined",导致整个"自动生成用户信息"崩溃。
      // 后端已修复不再产生这类记录,这里仍做空值防护:去重集合缺一个用户名只是
      // 极小概率生成重名(提交时后端还有幂等保护),但页面崩溃是硬故障。
      const existingUsernames = new Set(
        existingUsersResp.users
          .map((u) => (u.username ?? "").toLowerCase())
          .filter((name) => name.length > 0)
      );
      // 输入框里已有的行(可能是用户手动粘贴的、也可能是上一次生成还没提交的)
      // 也要一起去重,否则连续点两次"自动生成"会产出重复用户名。
      for (const row of parseCsvUsers(csvText, batchAutoActivate)) {
        if (row.username) existingUsernames.add(row.username.toLowerCase());
      }

      const generated = generateUsers(genCount, domain, existingUsernames);
      const autoActivateCol = batchAutoActivate ? "是" : "否";
      const newLines = generated
        .map(
          (u) =>
            `${accountId},${u.username},${u.given_name},${u.family_name},${u.email},${genTier},${autoActivateCol}`
        )
        .join("\n");

      // 判断 textarea 里是否全是「其他账号」的数据:若是,说明上一批已提交(或用户
      // 手动切换了账号),此时应当覆盖而不是追加——追加会把两个不同账号的数据混在
      // 同一份 CSV 里一起提交,容易误操作。
      // 判断依据:把当前 CSV 解析出来,取所有行的 account_id;若全都不等于当前选择的
      // accountId(且不为空),则视为"跨账号",直接覆盖。
      const existingRows = parseCsvUsers(csvText, batchAutoActivate);
      const existingAccountIds = new Set(existingRows.map((r) => r.account_id).filter(Boolean));
      const isCrossAccount =
        existingRows.length > 0 &&
        existingAccountIds.size > 0 &&
        !existingAccountIds.has(accountId);

      setCsvText((prev) =>
        isCrossAccount || !prev.trim()
          ? newLines
          : `${prev.trim()}\n${newLines}`
      );
    } catch (err) {
      setGenError(err instanceof Error ? err.message : "自动生成失败");
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div>
      <h2 className="section-title">批量建号</h2>

      <div className="card">
        <div className="section-title">单条建号</div>
        {singleError && <div className="error-banner">{singleError}</div>}
        {singleResult && (
          <div className="card" style={{ background: "#f0fdf4", color: "#166534" }}>
            {singleResult}
          </div>
        )}
        <form onSubmit={handleSingleSubmit}>
          <div className="form-row">
            <label>目标账号</label>
            <select
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              required
            >
              <option value="">请选择</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_id}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>用户名</label>
            <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
          </div>
          <div className="form-row">
            <label>名</label>
            <input value={form.given_name} onChange={(e) => setForm({ ...form, given_name: e.target.value })} required />
          </div>
          <div className="form-row">
            <label>姓</label>
            <input value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} required />
          </div>
          <div className="form-row">
            <label>邮箱</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              placeholder="username@kiro-users.example.com"
            />
          </div>
          <div className="form-row">
            <label>层级</label>
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>是否自动激活</label>
            <select
              value={form.auto_activate === false ? "no" : "yes"}
              onChange={(e) => setForm({ ...form, auto_activate: e.target.value !== "no" })}
            >
              <option value="yes">是(默认)</option>
              <option value="no">否</option>
            </select>
          </div>
          <p style={{ fontSize: 12, color: "#64748b", marginTop: -8 }}>
            选择"否"时不会执行"登录→填用户名→接收验证码→填入新密码"这一套自动化动作,
            该用户直接标记为人工处理,由客户自行完成登录和改密。主要用于邮箱不是平台
            控制的接码邮箱(平台收不到验证码邮件)的场景。
          </p>
          <button className="primary" type="submit" disabled={singleLoading}>
            {singleLoading ? "提交中..." : "建号"}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="section-title">批量建号(粘贴 CSV)</div>
        <p style={{ fontSize: 12, color: "#64748b" }}>
          每行格式: account_id,username,given_name,family_name,email,tier,auto_activate(§3.2)。
          最后一列(auto_activate)可省略,省略时使用下方"是否自动激活"下拉框的值。
          行数较多时会自动分批提交(每批最多 {BATCH_CREATE_CHUNK_SIZE} 条),并显示进度;
          提交前会先检查同一目标账号下的邮箱/用户名是否重复,有重复则整份不提交并指出具体行号。
        </p>

        <div className="form-row">
          <label>是否自动激活(适用于未在 CSV 行内单独指定的记录)</label>
          <select
            value={batchAutoActivate ? "yes" : "no"}
            onChange={(e) => setBatchAutoActivate(e.target.value !== "no")}
          >
            <option value="yes">是(默认)</option>
            <option value="no">否</option>
          </select>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 12,
            flexWrap: "wrap",
            padding: 12,
            marginBottom: 12,
            background: "#f8fafc",
            borderRadius: 6,
          }}
        >
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>目标账号</label>
            <select value={genAccountId} onChange={(e) => setGenAccountId(e.target.value)}>
              <option value="">(默认第一个)</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>层级</label>
            <select value={genTier} onChange={(e) => setGenTier(e.target.value)}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>生成数量</label>
            <input
              type="number"
              min={1}
              max={500}
              value={genCount}
              onChange={(e) => setGenCount(Number(e.target.value))}
              style={{ width: 80 }}
            />
          </div>
          <button type="button" onClick={handleAutoGenerate} disabled={genLoading}>
            {genLoading ? "生成中..." : "自动生成用户信息"}
          </button>
          {genError && <span style={{ color: "#dc2626", fontSize: 12 }}>{genError}</span>}
        </div>

        {batchError && (
          <div className="error-banner" style={{ whiteSpace: "pre-line" }}>
            {batchError}
          </div>
        )}
        <form onSubmit={handleBatchSubmit}>
          <textarea
            style={{ width: "100%", height: 140, fontFamily: "monospace" }}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder="222222222222,alice,Alice,Wang,alice@kiro-users.example.com,pro,是"
          />
          <div style={{ marginTop: 8 }}>
            <button className="primary" type="submit" disabled={batchLoading}>
              {batchLoading ? "提交中..." : "批量建号"}
            </button>
          </div>
        </form>

        {batchProgress && (
          <p style={{ fontSize: 12, color: "#0369a1", marginTop: 8 }}>
            正在提交第 {batchProgress.batchIndex}/{batchProgress.totalBatches} 批,已处理{" "}
            {batchProgress.processed}/{batchProgress.total} 条...
          </p>
        )}

        {batchResult && (
          <div style={{ marginTop: 12 }}>
            <p>
              共 {batchResult.total} 条,成功 {batchResult.succeeded} 条,失败 {batchResult.failed} 条
            </p>
            {(batchResult.activation_dispatch_failed ?? 0) > 0 && (
              <div
                className="card"
                style={{ background: "#fffbeb", color: "#92400e", fontSize: 12 }}
              >
                其中 {batchResult.activation_dispatch_failed} 条建号已成功、台账已写入,但激活任务
                投递失败,这些用户不会自动进入激活流程。可直接重新提交整份 CSV(建号幂等,
                会重新投递激活任务),或在用户列表页人工处理。
              </div>
            )}
            <table>
              <thead>
                <tr>
                  <th>邮箱</th>
                  <th>结果</th>
                  <th>说明</th>
                </tr>
              </thead>
              <tbody>
                {batchResult.results.map((r) => (
                  <tr key={`${r.account_id}#${r.email}`}>
                    <td>{r.email}</td>
                    <td>{r.success ? "成功" : "失败"}</td>
                    <td>
                      {r.error ??
                        (r.activation_dispatch_error
                          ? `建号成功,但激活任务投递失败: ${r.activation_dispatch_error}`
                          : r.already_existed
                            ? "已存在,幂等跳过"
                            : "-")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
